import { format } from 'date-fns';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Utility per gestire le chiamate HTTP e gli errori con async/await
export const fetchWithHandling = async (
    uri: string,
    options: RequestInit,
    onUnauthorized: () => void,
    isNotJsonResponse?: boolean
): Promise<any | null> => {
    try {
        const response = await fetch(uri, options);

        // Gestione dello stato HTTP 401 (Unauthorized)
        if (response.status === 401) {
            onUnauthorized();
            return null; // Ritorna null in caso di errore 401
        }

        // Gestione di altri errori HTTP (da 400 a 599)
        if (response.status >= 400 && response.status < 600) {
            let errorMessage = "Si è verificato un errore. Riprova.";
            if (response.status === 403) {
                errorMessage = "Accesso negato. Non hai i permessi necessari.";
            } else if (response.status === 404) {
                errorMessage = "Risorsa non trovata.";
            } else if (response.status === 500) {
                errorMessage = "Errore interno del server. Riprova.";
            } else if (response.status === 413) {
                errorMessage = "I file caricati sono troppo grandi. Riprova con un file più piccolo.";
            }

            toast.error(errorMessage, {
                position: "bottom-right"
            });

            return null; // Ritorna null in caso di errori
        }

        if (options.method !== "DELETE") {
            if (isNotJsonResponse) {
                return response;
            } else {
                return await response.json();
            }
        } else {
            return response;
        }

    } catch (error) {
        console.error("Errore nella chiamata HTTP: ", error);

        toast.error("Errore di rete, riprova più tardi", {
            position: "bottom-right"
        });

        return null; // Ritorna null in caso di eccezioni
    }
};

export const formatDateDDmmYYYY = (dateString: string): string => {
    const date = new Date(dateString);
    return format(date, 'dd/MM/yyyy');
};

export const formatDateYYYYmmDD = (dateString: string): string => {
    const date = new Date(dateString);
    return format(date, 'yyyy-MM-dd');
};

export const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return `${format(date, 'dd/MM/yyyy')} alle ${format(date, 'HH:mm')}`;
};

// Backend LocalTime values can carry seconds/nanoseconds precision (e.g. "14:30:45.123456") depending
// on when they were captured. <input type="time"> and the report views only ever need "HH:mm" — trim
// here so it's always clean regardless of what the backend happens to send.
export const toHHmm = (time?: string | null): string => {
    if (!time) return '';
    return time.slice(0, 5);
};

// Funzione per validare il Codice Fiscale
export function validateItalianTaxCode(taxCode: string): boolean {
    const taxCodeRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
    return taxCodeRegex.test(taxCode.toUpperCase());
}

// Funzione per validare la Partita IVA
export function validateItalianVAT(vatNumber: string): boolean {
    // Controlla se la Partita IVA è composta esattamente da 11 cifre numeriche
    return /^\d{11}$/.test(vatNumber);
}

export function validateEmail(email: string): boolean {
    // Regex per verificare la validità di un'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

export const bigCalendarMessages = {
    date: "Data",
    time: "Ora",
    event: "Evento",
    allDay: "Tutto il giorno",
    week: "Settimana",
    work_week: "Settimana lavorativa",
    day: "Giorno",
    month: "Mese",
    previous: "Precedente",
    next: "Successivo",
    yesterday: "Ieri",
    tomorrow: "Domani",
    today: "Oggi",
    agenda: "Agenda",
    noEventsInRange: "Non ci sono eventi in questo intervallo",
    showMore: (count: any) => `+ altri ${count}`,
};

export const truncateName = (name: string, maxLength: number = 20): string => {
    if (name.length <= maxLength) return name;
    const start = name.slice(0, maxLength - 3);
    return `${start}...`;
};

export const isUrl = (str: string): boolean => {
    try { new URL(str); return true; } catch { return false; }
};

export const buildMapsUrl = (address: string): string =>
    isUrl(address) ? address : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export const validateAddress = (address: string): boolean => {
    if (!address.trim()) return false;
    if (isUrl(address)) return true;
    return address.trim().length >= 10 && /\d/.test(address);
};